/**
 * Author: Claude Code
 * Version: v3.2.98
 * Modified: 2025-10-13
 *
 * LoadingProgressManager - P0.4 Loading Progress Bar UI (即时更新版)
 * 显示5阶段加载进度，直接响应事件更新（无Tween动画）
 *
 * v3.2.98更新: 移除Tween动画，直接更新进度
 * - 事件层面已经50ms平滑更新
 * - 高清文本配置（更大字号）
 */

import * as Phaser from 'phaser';

export class LoadingProgressManager {
  private scene: Phaser.Scene;
  private container: any = null; // Rex UI Sizer
  private progressBar: Phaser.GameObjects.Graphics | null = null;
  private progressBarBg: Phaser.GameObjects.Graphics | null = null;
  private progressText: Phaser.GameObjects.Text | null = null;
  private stageText: Phaser.GameObjects.Text | null = null;
  private messageText: Phaser.GameObjects.Text | null = null;

  private currentPercentage: number = 0;
  private targetPercentage: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  create(): void {
    if (!this.scene.rexUI) {
      console.error('❌ LoadingProgressManager: Rex UI plugin not found!');
      return;
    }

    console.log('🎮 LoadingProgressManager: Creating progress bar UI');

    const centerX = this.scene.cameras.main.width / 2;
    const centerY = this.scene.cameras.main.height / 2;

    // Progress bar dimensions
    const barWidth = 600;
    const barHeight = 40;
    const barX = centerX - barWidth / 2;
    const barY = centerY - barHeight / 2;

    // Progress bar background
    this.progressBarBg = this.scene.add.graphics();
    this.progressBarBg.fillStyle(0x222222, 1);
    this.progressBarBg.fillRoundedRect(barX, barY, barWidth, barHeight, 10);
    this.progressBarBg.lineStyle(2, 0x444444, 1);
    this.progressBarBg.strokeRoundedRect(barX, barY, barWidth, barHeight, 10);

    // Progress bar fill
    this.progressBar = this.scene.add.graphics();
    this.updateProgressBar(0);

    // Progress percentage text - 高清晰度
    this.progressText = this.scene.add.text(
      centerX,
      centerY,
      '0%',
      {
        fontSize: '24px',
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }
    ).setOrigin(0.5);

    // Stage indicator text - 高清晰度
    this.stageText = this.scene.add.text(
      centerX,
      centerY - 70,
      'Stage 0/5',
      {
        fontSize: '18px',
        color: '#aaaaaa',
        fontFamily: 'Arial, sans-serif'
      }
    ).setOrigin(0.5);

    // Loading message text - 高清晰度
    this.messageText = this.scene.add.text(
      centerX,
      centerY + 70,
      'Preparing to load...',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial, sans-serif'
      }
    ).setOrigin(0.5);

    console.log('✅ LoadingProgressManager: Progress bar created');
  }

  /**
   * 处理loading:started事件
   */
  onLoadingStarted(event: any): void {
    console.log('🎬 LoadingProgressManager: Loading started', event.data);

    const { totalStages, participantCount } = event.data;

    if (this.stageText) {
      this.stageText.setText(`Stage 0/${totalStages}`);
    }

    if (this.messageText) {
      this.messageText.setText(`Initializing game for ${participantCount} players...`);
    }
  }

  /**
   * 处理loading:progress事件
   */
  onLoadingProgress(event: any): void {
    const { stage, stageIndex, totalStages, percentage, message } = event.data;

    // 直接更新进度（已经在事件层面平滑）
    this.currentPercentage = percentage;
    this.targetPercentage = percentage;

    // Update stage text
    if (this.stageText) {
      this.stageText.setText(`Stage ${stageIndex}/${totalStages}: ${stage}`);
    }

    // Update message text
    if (this.messageText) {
      this.messageText.setText(message);
    }

    // Update progress bar immediately
    this.updateProgressBar(this.currentPercentage);
    if (this.progressText) {
      this.progressText.setText(`${Math.floor(this.currentPercentage)}%`);
    }
  }

  /**
   * 处理loading:completed事件
   */
  onLoadingCompleted(event: any): void {
    console.log('✅ LoadingProgressManager: Loading completed', event.data);

    const { totalDuration } = event.data;

    // Set to 100%
    this.currentPercentage = 100;
    this.targetPercentage = 100;
    this.updateProgressBar(100);

    if (this.progressText) {
      this.progressText.setText('100%');
    }

    // Update texts
    if (this.stageText) {
      this.stageText.setText('Loading Complete!');
    }

    if (this.messageText) {
      this.messageText.setText(`Loaded in ${(totalDuration / 1000).toFixed(1)}s - Starting game...`);
    }
  }

  /**
   * 更新进度条图形
   */
  private updateProgressBar(percentage: number): void {
    if (!this.progressBar) return;

    const centerX = this.scene.cameras.main.width / 2;
    const centerY = this.scene.cameras.main.height / 2;
    const barWidth = 600;
    const barHeight = 40;
    const barX = centerX - barWidth / 2;
    const barY = centerY - barHeight / 2;

    const fillWidth = (barWidth - 8) * (percentage / 100);

    // Clear and redraw
    this.progressBar.clear();

    // Gradient color based on progress
    let color = 0x4a9eff; // Blue
    if (percentage >= 80) {
      color = 0x4ade80; // Green
    } else if (percentage >= 40) {
      color = 0x60a5fa; // Light blue
    }

    this.progressBar.fillStyle(color, 1);
    this.progressBar.fillRoundedRect(barX + 4, barY + 4, fillWidth, barHeight - 8, 8);
  }

  /**
   * 显示进度条
   */
  show(): void {
    if (this.progressBarBg) this.progressBarBg.setVisible(true);
    if (this.progressBar) this.progressBar.setVisible(true);
    if (this.progressText) this.progressText.setVisible(true);
    if (this.stageText) this.stageText.setVisible(true);
    if (this.messageText) this.messageText.setVisible(true);
  }

  /**
   * 隐藏进度条
   */
  hide(): void {
    if (this.progressBarBg) this.progressBarBg.setVisible(false);
    if (this.progressBar) this.progressBar.setVisible(false);
    if (this.progressText) this.progressText.setVisible(false);
    if (this.stageText) this.stageText.setVisible(false);
    if (this.messageText) this.messageText.setVisible(false);
  }

  /**
   * 清理资源
   */
  destroy(): void {
    console.log('🛑 LoadingProgressManager: Destroying');

    if (this.progressBarBg) {
      this.progressBarBg.destroy();
      this.progressBarBg = null;
    }

    if (this.progressBar) {
      this.progressBar.destroy();
      this.progressBar = null;
    }

    if (this.progressText) {
      this.progressText.destroy();
      this.progressText = null;
    }

    if (this.stageText) {
      this.stageText.destroy();
      this.stageText = null;
    }

    if (this.messageText) {
      this.messageText.destroy();
      this.messageText = null;
    }

    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }
}
